import React, { useState, useContext, useEffect, useCallback } from 'react'
import Script from 'next/script'
import { UserContext } from '../../context/UserContext';
import { criteriaSentence } from '../../js/criteria-phrase';
import { loadGoogleLocationAutocomplete } from '../../service/location';
import { googleApiKey, s3Static } from '../../utils/constants';
import { RequestContext } from '../../context/RequestContext';
import Image from 'next/image';
import { dismissModal } from '../../service/modal-service';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Check, Circle } from 'lucide-react';

// Animation variants - simplified to prevent layout shifts
const questionVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.2,
            ease: "easeOut"
        }
    },
    exit: { 
        opacity: 0,
        transition: {
            duration: 0.15
        }
    }
};

const choiceContainerVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: {
            staggerChildren: 0.03,
            delayChildren: 0.05
        }
    }
};

const choiceItemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.15
        }
    }
};

// Modern Radio Card Component - Borderless Compact
const RadioCard = ({ choice, isSelected, onChange, hasImage }) => {
    return (
        <motion.div
            variants={choiceItemVariants}
            whileTap={{ scale: 0.98 }}
        >
            <label className="block cursor-pointer">
                <input 
                    type="radio" 
                    name="optCriteria" 
                    checked={isSelected} 
                    value={choice.value} 
                    onChange={onChange}
                    className="sr-only"
                />
                <div className={`
                    relative px-2 py-1.5 rounded-md
                    transition-all duration-150 ease-out
                    ${isSelected 
                        ? 'bg-orange-50' 
                        : 'hover:bg-gray-50'
                    }
                `}>
                    <div className="flex items-center gap-2.5">
                        {/* Custom Radio Circle */}
                        <div className={`
                            shrink-0 w-4 h-4 rounded-full
                            border-2 transition-all duration-150
                            flex items-center justify-center
                            ${isSelected 
                                ? 'border-orange-500 bg-orange-500' 
                                : 'border-gray-300'
                            }
                        `}>
                            {isSelected && (
                                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            )}
                        </div>
                        
                        {/* Content */}
                        <span className={`
                            flex-1 text-sm
                            ${isSelected ? 'text-orange-700 font-medium' : 'text-gray-700'}
                        `}>
                            {choice.value}
                        </span>
                    </div>
                    
                    {/* Image if present */}
                    {choice.image && (
                        <div className="mt-1.5 flex justify-center">
                            <Image
                                src={s3Static + choice.image}
                                width={60}
                                height={60}
                                style={{ objectFit: 'contain' }}
                                alt={choice.value}
                            />
                        </div>
                    )}
                </div>
            </label>
        </motion.div>
    );
};

// Modern Checkbox Card Component - Borderless Compact
const CheckboxCard = ({ choice, isSelected, onChange }) => {
    return (
        <motion.div
            variants={choiceItemVariants}
            whileTap={{ scale: 0.98 }}
        >
            <label className="block cursor-pointer">
                <input 
                    type="checkbox" 
                    name="optCriteria" 
                    value={choice.value} 
                    onChange={onChange}
                    checked={isSelected}
                    className="sr-only"
                />
                <div className={`
                    relative px-2 py-1.5 rounded-md
                    transition-all duration-150 ease-out
                    ${isSelected 
                        ? 'bg-orange-50' 
                        : 'hover:bg-gray-50'
                    }
                `}>
                    <div className="flex items-center gap-2.5">
                        {/* Custom Checkbox */}
                        <div className={`
                            shrink-0 w-4 h-4 rounded
                            border-2 transition-all duration-150
                            flex items-center justify-center
                            ${isSelected 
                                ? 'border-orange-500 bg-orange-500' 
                                : 'border-gray-300'
                            }
                        `}>
                            {isSelected && (
                                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            )}
                        </div>
                        
                        {/* Content */}
                        <span className={`
                            flex-1 text-sm
                            ${isSelected ? 'text-orange-700 font-medium' : 'text-gray-700'}
                        `}>
                            {choice.value}
                        </span>
                    </div>
                </div>
            </label>
        </motion.div>
    );
};

// Modern Input Component - Compact
const ModernInput = ({ type, placeholder, value, onChange, icon: Icon, id, min, max }) => {
    const [isFocused, setIsFocused] = useState(false);
    
    return (
        <div className={`
            relative rounded-lg overflow-hidden transition-all duration-200
            ${isFocused ? 'shadow-md' : 'shadow-sm'}
        `}>
            {Icon && (
                <div className={`
                    absolute inset-y-0 left-0 flex items-center pl-3
                    ${isFocused ? 'text-orange-500' : 'text-gray-400'}
                `}>
                    <Icon className="w-4 h-4" />
                </div>
            )}
            <input
                id={id}
                type={type}
                className={`
                    w-full px-3 py-2.5 ${Icon ? 'pl-10' : ''}
                    text-gray-700 placeholder-gray-400
                    bg-white rounded-lg
                    border-2 transition-all duration-200
                    ${isFocused 
                        ? 'border-orange-400' 
                        : 'border-gray-100 hover:border-orange-200'
                    }
                    focus:outline-none
                `}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                min={min}
                max={max}
            />
        </div>
    );
};

// Modern Date Input Component - Compact
const ModernDateInput = ({ value, onChange, min, max }) => {
    const [isFocused, setIsFocused] = useState(false);
    
    return (
        <div className="flex justify-center">
            <div className={`
                relative rounded-lg overflow-hidden transition-all duration-200
                ${isFocused ? 'shadow-md' : 'shadow-sm'}
            `}>
                <div className={`
                    absolute inset-y-0 left-0 flex items-center pl-3
                    pointer-events-none
                    ${isFocused ? 'text-orange-500' : 'text-gray-400'}
                `}>
                    <Calendar className="w-4 h-4" />
                </div>
                <input
                    type="date"
                    value={value}
                    min={min}
                    max={max}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className={`
                        w-48 px-3 py-2.5 pl-10
                        text-gray-700
                        bg-white rounded-lg
                        border-2 transition-all duration-200
                        ${isFocused 
                            ? 'border-orange-400' 
                            : 'border-gray-100 hover:border-orange-200'
                        }
                        focus:outline-none cursor-pointer
                    `}
                    required
                />
            </div>
        </div>
    );
};

export const ServiceCriteria = (props) => {
    const requestCategorySettings = props.requestCategorySettings
    const goToSection3 = props.goToSection3
    const location = props.location
    const countryCd = props.countryCd
    const setRequestForm = props.setRequestForm

    const [ selectedItems, setSelectedItems ] = useState([])
    const [ checkedItems, setCheckedItems ] = useState("")
    const [ criteriaDate, setCriteriaDate ] = useState("")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")

    const [ choices, setChoices ] = useState([]); 
    const [ question, setQuestion ] = useState("");
    const [ questionImage, setQuestionImage ] = useState("");
    const [ criteria, setCriteria ] = useState("");
    const [ inputType, setInputType ] = useState("");
    const [ nextQuestionCode, setNextQuestionCode ] = useState("")
    const [ questionsList, setQuestionsList ] = useState("");
    const [ questionCode, setQuestionCode ] = useState("");
    const [ googleAutoComplete, setGoogleAutoComplete ] = useState(false); 
    const [ isLoadGoogleMap, setIsLoadGoogleMap ] = useState(false)
    const [ questionKey, setQuestionKey ] = useState(0); // For animation key

    const { requestType, setRequestType, setRequestCriteria, setServiceItems, headline, setHeadline, setServiceSummary } = useContext(RequestContext); 

    const { } = useContext(UserContext);    

    const filterCriteria = useCallback(() => {	
        for (let temp in requestCategorySettings){
            if (requestCategorySettings[temp].requestType == requestType){
                setQuestionsList(requestCategorySettings[temp].criteriaQuestions)
                setHeadline(requestCategorySettings[temp].headline)
                setQuestion(requestCategorySettings[temp].criteriaQuestions[0].question);
                setQuestionCode(requestCategorySettings[temp].criteriaQuestions[0].question_code)
                setChoices(requestCategorySettings[temp].criteriaQuestions[0].choices);
                setInputType(requestCategorySettings[temp].criteriaQuestions[0].inputType);
                setSelectedItems([])
                if(requestCategorySettings[temp].criteriaQuestions[0].inputType != "list"){
                    setNextQuestionCode(requestCategorySettings[temp].criteriaQuestions[0].next_question_code)
                } else {
                    setCriteria(requestCategorySettings[temp].criteriaQuestions[0].choices[0].value);
                    setNextQuestionCode(requestCategorySettings[temp].criteriaQuestions[0].choices[0].next_question_code)
                }
                if(requestCategorySettings[temp].criteriaQuestions[0].inputType == 'location'){
                    setGoogleAutoComplete(true);
                }
            }
       }
    },[requestType, requestCategorySettings, setHeadline, setNextQuestionCode])	
    
    const setDefaults = useCallback(() => {
        switch (requestType){
   
            case  "fitness" ||  "personal-trainer":
                setRequestType("Fitness Trainer");
                break;
           
            case  "real-estate" || "property-agent":
                setRequestType("Housing");
                break;
           
            case  "aircon-servicing-singapore":
                setRequestType("Aircon Services")
                break;
            
            case  "domestic-helper":
                setRequestType("Domestic Services")
                break;
    
            case  "handyman":
                setRequestType("Handyman Services");
                break;
    
            case  "financial-adviser-singapore":
                setRequestType("Financial");
                break;
        }
        filterCriteria();  
    }, [requestType, setRequestType, filterCriteria])
   
    const changeCriteria = (choice) => {
        setCriteria(choice.value);
        setNextQuestionCode(choice.next_question_code)
    };
   
    const setNextQuestion = (nextQuestionCode) => {        
        if ( nextQuestionCode === "none"){
            goToSection3();
        } else {
            setQuestionKey(prev => prev + 1); // Trigger animation
            for(let i in questionsList){
                if(questionsList[i].question_code === nextQuestionCode){
                    setQuestion(questionsList[i].question);
                    if(questionsList[i].image){
                        setQuestionImage(questionsList[i].image)
                    } else {
                        setQuestionImage("")
                    }
                    setQuestionCode(questionsList[i].question_code);
                    setChoices(questionsList[i].choices);
                    setInputType(questionsList[i].inputType);
                    if(questionsList[i].inputType === 'list' || questionsList[i].inputType === 'select'){
                        if(questionsList[i].inputType === 'list'){
                            setCriteria(questionsList[i].choices[0].value);
                        }
                        setNextQuestionCode(questionsList[i].choices[0].next_question_code)
                    } else {
                        setNextQuestionCode(questionsList[i].next_question_code)                        
                    }
                    if( questionsList[i].inputType == 'location'){
                        setGoogleAutoComplete(true);
                    }
                    if( questionsList[i].inputType == 'date'){
                        setCriteria(criteriaDate);
                    }
                }
            }            
        }
    }
   
    const addCriteria = () => {	  
        if ( criteria != ""){	 

            let criteriaPhrase = criteriaSentence(questionCode, requestType, criteria);
            
            let requestCriteriaW

            if(inputType === "date"){
                const date = new Date(criteria)
                requestCriteriaW = 
                {
                    'criteria': criteriaPhrase,
                    [questionCode]: date
                }
            } else {
                if(inputType === "select"){
                    requestCriteriaW = 
                    {
                        'criteria': criteriaPhrase,
                        [questionCode]: criteria
                    }
                    setSelectedItems([])
                    setCheckedItems("")
                } else {
                    requestCriteriaW = 
                    {
                        'criteria': criteriaPhrase,
                        [questionCode]: criteria
                    }
                }
            }
            // Replace any earlier answer to this question: Back from the date step
            // returns to the last question, and answering it again must not
            // leave the old answer in the request as well.
            setRequestCriteria(requestCriteria => [...requestCriteria.filter(c => c[questionCode] === undefined), requestCriteriaW])
            setCriteria("");
            setNextQuestion(nextQuestionCode);	
            		 
        } else {
            skipCriteria(nextQuestionCode);
        }
    }
        
    const skipCriteria = () => {
        setCriteria("");
        setNextQuestion(nextQuestionCode);
    }
       
    const updateResponse = (e) =>{
        setCriteria(e.target.value)
    }
    
    const handleDateChange = (e) =>{
        setCriteria(e.target.value)
    }
    
    const handleCheck = (event, choice) => {
        let checkedItems = [...selectedItems]

        if (event.target.checked) {
            checkedItems = [...checkedItems, event.target.value]
        } else {
            let index = checkedItems.indexOf(event.target.value)
            if(index < 0) return
            checkedItems.splice(index, 1)
        }
                
        setSelectedItems(checkedItems)
        
        setCriteria(checkedItems?.length ? checkedItems.reduce((total, item) => {
            return total + ", " + item;
        })
        : "");
        
        setNextQuestionCode(choice.next_question_code)
    };

    useEffect(() => {
        if(!countryCd || !isLoadGoogleMap) return;
        loadGoogleLocationAutocomplete(countryCd);
        setCriteria(location);
    }, [isLoadGoogleMap, countryCd, location])

    useEffect(() => {
        if(requestCategorySettings.length == 0){
            return;
        }
        if(requestType != undefined) {
            setDefaults();
            setRequestCriteria([])
            setServiceItems([])
            setServiceSummary("")
        }
    }, [requestCategorySettings, requestType, setRequestCriteria, setServiceItems, setDefaults, setServiceSummary])

    useEffect(() => {
        if(inputType === "date"){
            const minD = new Date()
            setMinDate(minD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
            
            const sDate = new Date()
            sDate.setDate(sDate.getDate() + 3)
            setCriteriaDate(sDate.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
            setCriteria(sDate.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))

            const maxD = new Date()
            maxD.setDate(maxD.getDate() + 182)
            setMaxDate(maxD.toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10))
        }
    }, [inputType])

    const reset = () => {
        if(setRequestForm){
            dismissModal()
        }else{
            window.history.back()
        }
    }

    return (
        <div className="max-w-xl mx-auto">
            {/* Headline */}
            {headline && (
                <div className='text-center mb-3 px-3'>
                    <div 
                        className='text-gray-600 text-sm leading-relaxed wrap-break-word whitespace-pre-wrap' 
                        dangerouslySetInnerHTML={{ __html: headline }}
                    />
                </div>
            )}
            
            <div className='px-3'>
                <AnimatePresence mode="wait">
                    <motion.div 
                        key={questionKey}
                        className="max-w-2xl mx-auto"
                        variants={questionVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        {/* Question */}
                        <div className="mb-3">
                            {question ? (
                                <h2 className="text-base sm:text-lg font-semibold text-gray-800 text-center">
                                    {question}
                                </h2>
                            ) : (
                                <div className="text-gray-500 text-center text-sm italic">
                                    Anything else you want us to include?
                                </div>
                            )}
                        </div>
                        
                        {/* Question Image */}
                        {questionImage && (
                            <div className='flex justify-center mb-3'>
                                <div className="rounded-lg overflow-hidden">
                                    <Image
                                        src={s3Static + questionImage}
                                        width={80}
                                        height={80}
                                        style={{ objectFit: 'contain' }}
                                        alt={question}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Choices / Inputs */}
                        {question ? (
                            <div className="mt-2">
                                {choices.length > 0 ? (
                                    <>
                                        {/* Radio List */}
                                        {inputType === 'list' && (
                                            <motion.div 
                                                className='space-y-0.5'
                                                variants={choiceContainerVariants}
                                                initial="initial"
                                                animate="animate"
                                            >
                                                {[...choices].map((choice, i) => (
                                                    <RadioCard
                                                        key={i}
                                                        choice={choice}
                                                        isSelected={choice.value === criteria}
                                                        onChange={() => changeCriteria(choice)}
                                                        hasImage={!!choice.image}
                                                    />
                                                ))}
                                            </motion.div>
                                        )}
                                        
                                        {/* Checkbox Select */}
                                        {inputType === 'select' && (
                                            <motion.div 
                                                className='space-y-0.5'
                                                variants={choiceContainerVariants}
                                                initial="initial"
                                                animate="animate"
                                            >
                                                {[...choices].map((choice, i) => (
                                                    <CheckboxCard
                                                        key={i}
                                                        choice={choice}
                                                        isSelected={selectedItems.includes(choice.value)}
                                                        onChange={(e) => handleCheck(e, choice)}
                                                    />
                                                ))}
                                            </motion.div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Text Input */}
                                        {(inputType === 'open' || inputType === 'text') && (
                                            <ModernInput
                                                type="text"
                                                placeholder={question}
                                                value={criteria}
                                                onChange={updateResponse}
                                            />
                                        )}
                                        
                                        {/* Number Input */}
                                        {inputType === 'number' && (
                                            <ModernInput
                                                type="number"
                                                placeholder={question}
                                                value={criteria}
                                                onChange={updateResponse}
                                            />
                                        )}
                                        
                                        {/* Location Input */}
                                        {inputType === 'location' && (
                                            <ModernInput
                                                id="searchLocationField"
                                                type="text"
                                                placeholder={question}
                                                value={criteria}
                                                onChange={updateResponse}
                                                icon={MapPin}
                                            />
                                        )}
                                        
                                        {/* Date Input */}
                                        {inputType === 'date' && (
                                            <ModernDateInput
                                                value={criteria}
                                                onChange={handleDateChange}
                                                min={minDate}
                                                max={maxDate}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <ModernInput
                                type="text"
                                placeholder="Additional criteria (optional)"
                                value={criteria}
                                onChange={updateResponse}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
                
                {/* Navigation Buttons */}
                <div className="max-w-2xl mx-auto flex justify-between items-center mt-5 pt-3">
                    {question ? (
                        <>
                            {/* Back Button */}
                            <button 
                                type="button" 
                                onClick={() => reset()} 
                                aria-label="Go back"
                                className="flex items-center gap-1 px-3 py-2 text-gray-500 hover:text-orange-600 rounded-lg hover:bg-orange-50 transition-all duration-200"
                            >
                                <ChevronLeft className="w-5 h-5" />
                                <span className="font-medium">Back</span>
                            </button>
                            
                            {/* Next Button */}
                            <button 
                                aria-label="Next"
                                type="button" 
                                onClick={addCriteria}
                                className="flex items-center gap-1.5 px-6 py-3
                                    bg-linear-to-r from-orange-600 to-amber-500 
                                    hover:from-orange-500 hover:to-amber-500
                                    text-white font-semibold text-sm
                                    rounded-xl shadow-md
                                    transition-all duration-200"
                            >
                                <span className='text-xl'>Next</span>
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </>
                    ) : (
                        <button 
                            type="button"
                            onClick={addCriteria}
                            className="mx-auto px-5 py-2 border-2 border-orange-400 text-orange-600 text-sm font-semibold rounded-lg hover:bg-orange-50 transition-all duration-200"
                        >
                            Add Criteria
                        </button>
                    )}
                </div>
                
                {/* Final Next Button (when no question) */}
                {!question && (
                    <div className="flex justify-center mt-3">
                        <button 
                            type="button" 
                            onClick={goToSection3}
                            className="flex items-center gap-1.5 px-6 py-2.5
                                bg-linear-to-r from-orange-500 to-amber-500 
                                hover:from-orange-600 hover:to-amber-600
                                text-white font-semibold text-sm
                                rounded-lg shadow-md
                                transition-all duration-200"
                        >
                            <span>Continue</span>
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
            
            {googleAutoComplete && (
                <Script
                    id="google-maps"
                    src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&language=en&libraries=places&callback=Function.prototype`}
                    strategy="lazyOnload"
                    onLoad={() => setIsLoadGoogleMap(true)}
                />
            )}
        </div>
    )
}
